<template>
  <div v-if="canShow" class="public-link-wrapper">
    <button
      v-if="canCreate || canManage"
      type="button"
      :class="buttonClass"
      :disabled="loading"
      :title="buttonTitle"
      @click="openModal"
    >
      <ShareIcon :size="iconSize" color="currentColor" />
    </button>

    <div v-if="modalOpen" class="public-link-modal-backdrop" @click.self="closeModal">
      <div class="public-link-modal" role="dialog" aria-modal="true">
        <h3 class="public-link-title">
          {{
            linkState && !editing
              ? s__('Attachments|Public link')
              : linkState
                ? s__('Attachments|Edit public link')
                : s__('Attachments|Create public link')
          }}
        </h3>

        <div v-if="linkState && !editing" class="public-link-view">
          <label class="public-link-field">
            <span>{{ s__('Attachments|Public link') }}</span>
            <div class="public-link-copy-row">
              <input :value="absolutePublicUrl" readonly />
              <button
                type="button"
                class="btn-sm btn-secondary public-link-icon-btn"
                :disabled="!absolutePublicUrl"
                :title="s__('Attachments|Copy link')"
                @click="copyPublicLink"
              >
                <CopyIcon class="public-link-copy-icon" :size="16" color="#666" />
              </button>
              <a
                v-if="absolutePublicUrl"
                :href="absolutePublicUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="btn-sm btn-secondary public-link-icon-btn"
                :title="s__('Attachments|Open link')"
              >
                <ExternalLinkIcon :size="16" />
              </a>
            </div>
          </label>
          <p v-if="copyMessage" class="public-link-copy-message">{{ copyMessage }}</p>
          <p v-if="!absolutePublicUrl" class="public-link-error">
            {{
              s__('Attachments|This existing link cannot be shown. Save the link to regenerate it.')
            }}
          </p>
          <p class="public-link-meta">{{ linkDescription }}</p>
        </div>

        <template v-else>
          <p v-if="createdUrl" class="public-link-created">
            {{ s__('Attachments|Public link created:') }}
            <a :href="createdUrl" target="_blank" rel="noopener noreferrer">{{ createdUrl }}</a>
          </p>

          <label class="public-link-field">
            <span>{{ s__('Attachments|Password') }}</span>
            <input v-model="password" type="password" autocomplete="new-password" />
          </label>
          <label class="public-link-field">
            <span>{{ s__('Attachments|Confirm password') }}</span>
            <input v-model="passwordConfirmation" type="password" autocomplete="new-password" />
          </label>
          <label v-if="linkState" class="public-link-checkbox">
            <input v-model="clearPassword" type="checkbox" />
            <span>{{ s__('Attachments|Remove password protection') }}</span>
          </label>
          <label class="public-link-field">
            <span>{{ s__('Attachments|Expires at') }}</span>
            <DateTimeInput
              :datetime="expiresAt"
              :title="s__('Attachments|Expires at')"
              @update:datetime="expiresAt = $event"
            />
          </label>
        </template>

        <p v-if="error" class="public-link-error">{{ error }}</p>

        <div class="public-link-actions">
          <button type="button" class="btn-sm btn-secondary" @click="closeModal">
            {{ s__('Attachments|Cancel') }}
          </button>
          <button
            v-if="linkState && canManage"
            type="button"
            class="btn-sm btn-danger"
            :disabled="loading"
            @click="destroyLink"
          >
            {{ s__('Attachments|Destroy link') }}
          </button>
          <button
            v-if="linkState && canManage && !editing"
            type="button"
            class="btn-sm btn-primary"
            @click="startEditing"
          >
            <EditIcon :size="16" color="currentColor" />
            {{ s__('Attachments|Edit') }}
          </button>
          <button
            v-if="!linkState || editing"
            type="button"
            class="btn-sm btn-primary"
            :disabled="loading"
            @click="saveLink"
          >
            {{ linkState ? s__('Attachments|Save') : s__('Attachments|Create link') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { s__ } from '@/locale'
import DateTimeInput from './DateTimeInput.vue'
import CopyIcon from './icons/CopyIcon.vue'
import EditIcon from './icons/EditIcon.vue'
import ExternalLinkIcon from './icons/ExternalLinkIcon.vue'
import ShareIcon from './icons/ShareIcon.vue'

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  buttonClass: {
    type: String,
    default: 'btn-icon-action',
  },
  iconSize: {
    type: Number,
    default: 16,
  },
})

const PUBLIC_LINK_FIELDS = `
  id
  expiresAt
  passwordProtected
  publicUrl
  revoked
  expired
`

const CREATE_PUBLIC_LINK = gql`
  mutation CreateAttachmentPublicLink(
    $attachmentId: ID!
    $password: String
    $passwordConfirmation: String
    $expiresAt: ISO8601DateTime
  ) {
    createAttachmentPublicLink(
      attachmentId: $attachmentId
      password: $password
      passwordConfirmation: $passwordConfirmation
      expiresAt: $expiresAt
    ) {
      publicUrl
      publicLink {
        ${PUBLIC_LINK_FIELDS}
      }
      errors
    }
  }
`

const UPDATE_PUBLIC_LINK = gql`
  mutation UpdateAttachmentPublicLink(
    $id: ID!
    $password: String
    $passwordConfirmation: String
    $expiresAt: ISO8601DateTime
    $clearPassword: Boolean
  ) {
    updateAttachmentPublicLink(
      id: $id
      password: $password
      passwordConfirmation: $passwordConfirmation
      expiresAt: $expiresAt
      clearPassword: $clearPassword
    ) {
      publicLink {
        ${PUBLIC_LINK_FIELDS}
      }
      errors
    }
  }
`

const DESTROY_PUBLIC_LINK = gql`
  mutation DestroyAttachmentPublicLink($id: ID!) {
    destroyAttachmentPublicLink(id: $id) {
      success
      errors
    }
  }
`

const emit = defineEmits(['updated'])

const { mutate: createPublicLinkMutation } = useMutation(CREATE_PUBLIC_LINK)
const { mutate: updatePublicLinkMutation } = useMutation(UPDATE_PUBLIC_LINK)
const { mutate: destroyPublicLinkMutation } = useMutation(DESTROY_PUBLIC_LINK)

const modalOpen = ref(false)
const loading = ref(false)
const error = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const expiresAt = ref('')
const clearPassword = ref(false)
const createdUrl = ref('')
const copyMessage = ref('')
const editing = ref(false)
const localLink = ref(props.attachment.publicLink || null)

const linkState = computed(() => localLink.value || props.attachment.publicLink)
const canCreate = computed(() => props.attachment.canCreatePublicLink && !linkState.value)
const canManage = computed(() => props.attachment.canManagePublicLink && !!linkState.value)
const canShow = computed(() => !!linkState.value || canCreate.value)
const visiblePublicUrl = computed(() => linkState.value?.publicUrl || createdUrl.value || '')
const absolutePublicUrl = computed(() => {
  if (!visiblePublicUrl.value) return ''
  if (typeof window === 'undefined') return visiblePublicUrl.value

  return new URL(visiblePublicUrl.value, window.location.origin).toString()
})
const buttonTitle = computed(() =>
  linkState.value ? s__('Attachments|Manage public link') : s__('Attachments|Create public link')
)
const linkDescription = computed(() => {
  if (!linkState.value) return ''
  const protectionText = linkState.value.passwordProtected
    ? s__('Attachments|This link is password-protected')
    : s__('Attachments|This link is not password-protected')

  if (linkState.value.expiresAt) {
    const expirationText = s__('Attachments|Public link expires at %{expiresAt}').replace(
      '%{expiresAt}',
      new Date(linkState.value.expiresAt).toLocaleString()
    )
    return `${protectionText}. ${expirationText}.`
  }
  return `${protectionText}. ${s__('Attachments|Public link does not expire')}.`
})

const openModal = () => {
  error.value = ''
  createdUrl.value = ''
  password.value = ''
  passwordConfirmation.value = ''
  clearPassword.value = false
  expiresAt.value = linkState.value?.expiresAt || ''
  editing.value = !linkState.value
  copyMessage.value = ''
  modalOpen.value = true
}

const closeModal = () => {
  modalOpen.value = false
  editing.value = false
}

const startEditing = () => {
  editing.value = true
  error.value = ''
  copyMessage.value = ''
  password.value = ''
  passwordConfirmation.value = ''
  clearPassword.value = false
  expiresAt.value = linkState.value?.expiresAt || ''
}

const copyPublicLink = async () => {
  if (!absolutePublicUrl.value) return

  try {
    await navigator.clipboard.writeText(absolutePublicUrl.value)
    copyMessage.value = s__('Attachments|Link copied')
  } catch {
    copyMessage.value = s__('Attachments|Failed to copy link')
  }
}

const saveLink = async () => {
  if (password.value !== passwordConfirmation.value) {
    error.value = s__('Attachments|Password confirmation does not match')
    return
  }

  loading.value = true
  error.value = ''

  try {
    if (linkState.value) {
      const { data } = await updatePublicLinkMutation({
        id: linkState.value.id,
        password: password.value || null,
        passwordConfirmation: passwordConfirmation.value || null,
        expiresAt: parseInputDate(expiresAt.value),
        clearPassword: clearPassword.value,
      })
      handleMutationResult(data?.updateAttachmentPublicLink)
    } else {
      const { data } = await createPublicLinkMutation({
        attachmentId: props.attachment.id,
        password: password.value || null,
        passwordConfirmation: passwordConfirmation.value || null,
        expiresAt: parseInputDate(expiresAt.value),
      })
      createdUrl.value = data?.createAttachmentPublicLink?.publicUrl || ''
      handleMutationResult(data?.createAttachmentPublicLink, { keepOpen: !!createdUrl.value })
    }
  } catch (err) {
    error.value = err.message || s__('Attachments|Failed to save public link')
  } finally {
    loading.value = false
  }
}

const destroyLink = async () => {
  if (!linkState.value) return

  loading.value = true
  error.value = ''

  try {
    const { data } = await destroyPublicLinkMutation({ id: linkState.value.id })
    const result = data?.destroyAttachmentPublicLink
    if (result?.errors?.length) {
      error.value = result.errors.join(', ')
      return
    }
    localLink.value = null
    emit('updated', { attachmentId: props.attachment.id, publicLink: null })
    closeModal()
  } catch (err) {
    error.value = err.message || s__('Attachments|Failed to destroy public link')
  } finally {
    loading.value = false
  }
}

const handleMutationResult = (result, { keepOpen = false } = {}) => {
  if (result?.errors?.length) {
    error.value = result.errors.join(', ')
    return
  }

  localLink.value = result?.publicLink || null
  emit('updated', { attachmentId: props.attachment.id, publicLink: localLink.value })
  editing.value = false
  if (!keepOpen) closeModal()
}

const parseInputDate = value => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
</script>

<style scoped>
.public-link-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.public-link-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
}

.public-link-modal {
  width: min(100%, 420px);
  padding: 20px;
  border-radius: 12px;
  background: #fff;
  color: #333;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}

.public-link-title {
  margin: 0 0 16px;
  font-size: 18px;
}

.public-link-field {
  display: grid;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 14px;
}

.public-link-field input {
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.public-link-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
}

.public-link-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.public-link-copy-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 8px;
}

.public-link-copy-row input {
  min-width: 0;
}

.public-link-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
}

.public-link-icon-btn .public-link-copy-icon {
  color: #666 !important;
}

.public-link-meta,
.public-link-copy-message {
  margin: 8px 0 0;
  color: #666;
  font-size: 13px;
}

.public-link-copy-message {
  color: #065f46;
}

.public-link-created {
  padding: 10px;
  border-radius: 8px;
  background: #ecfdf5;
  color: #065f46;
  font-size: 14px;
  overflow-wrap: anywhere;
}

.public-link-error {
  color: #dc2626;
  font-size: 14px;
}

.btn-danger {
  background: #dc2626;
  color: #fff;
}
</style>
